<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="isDisabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    open-on-click
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{props}"
            name="activator"
            data-cy="list-podNodeItem-actions-copySelected-activator"
      />
    </template>

    <v-card
      v-if="unprocessablePods.length === 0 && deepUnprocessablePods.length === 0"
      data-cy="dialog-copySelected-modal"
    >
      <v-card-title
        class="d-flex justify-space-between"
        data-cy="dialog-copySelected-title"
      >
        {{ $t('routes.node.copyDialogCopySelected', itemsCount) }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="scrollable">
        <div v-if="browseNode && !waitingForFtpCheck" class="pb-2">
          <FolderCreateInline
            :parent-node="browseNode"
            data-cy-prefix="dialog-moveSelected"
            @created="handleFolderCreated"
          />
        </div>

        <div v-if="itemsCount > 0">
          <v-text-field
            :placeholder="$t('routes.node.typeFolderName')"
            variant="solo-filled"
            density="compact"
            color="primary"
            hide-details
            append-inner-icon="mdi-magnify"
            class="mb-2"
            v-model="searchQuery"
          />
        </div>

        <div v-if="itemsCount === 0 && shouldShowNoItems"
             class="bg-error"
             data-cy="dialog-copySelected-noItems"
        >
          {{ $t('routes.node.copyDialogNoItems') }}
        </div>
        <div v-if="errorMessage && errorCode !== 'ERR233'" class="bg-error my-2" data-cy="dialog-copySelected-error">
          {{ errorMessage }}
        </div>
        <div v-if="errorCode === 'ERR233' && errorItems" class="bg-error my-2">
          {{ $t('backend.ERR233', errorParams) }}
          <span v-if="errorItems.length === 1">
            {{ $t('routes.node.depthExceededTooDeep', errorItems[0].children.length) }}
            <DepthExceededChildren :children="errorItems[0].children" @navigate="dialog = false" />
          </span>
          <template v-else>
            {{ $t('routes.node.depthExceededTooDeep', 2) }}
            <ul class="mx-5 mt-1">
              <li v-for="item in errorItems" :key="item.parent.id">
                <strong>{{ item.parent.name }}</strong> &gt;
                <DepthExceededChildren :children="item.children" @navigate="dialog = false" />
              </li>
            </ul>
          </template>
        </div>

        <div v-if="browseNode" class="pb-2">
          <div v-if="isArchived" class="mt-4">
            <v-checkbox
              v-model="archiveItems"
              v-bind:label="$t('routes.node.archiveItems')"
              hide-details
            />
          </div>
        </div>

        <v-list
          v-if="itemsCount > 0"
          density="compact"
          v-model:selected="listGroupSelected"
          role="listbox"
          :aria-label="$t('routes.node.destinationFolder')"
        >
          <div v-if="browseNode" class="copy-selected-dialog-list-subheader"
                data-cy="dialog-copySelected-destination">
            <div>
              {{ $t('routes.node.copyDialogTo') }}
            </div>
            <p class="font-weight-bold">
              {{ browseNode.name }}
            </p>
          </div>
          <div class="copy-selected-dialog-list-subheader" v-else>
            {{ $t('routes.node.copyDialogSelectDestination') }}
          </div>

          <div v-if="nodeList.length > 0">
            <v-list-item
              v-for="node in nodeList"
              :key="node.id"
              @click="displayNode(node.id)"
              :disabled="isNodeDisabled(node)"
              :value="node.id"
              data-cy="dialog-copySelected-listItem"
            >
              <template v-slot:prepend>
                <v-icon :icon="getNodeIconName(node)"></v-icon>
              </template>

              <v-list-item-title>
                <v-tooltip location="bottom" :z-index="1200" :aria-label="node.name">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      {{ node.name }}
                    </span>
                  </template>
                  <span>{{ node.name }}</span>
                </v-tooltip>
              </v-list-item-title>
            </v-list-item>
          </div>
          <template v-else>
            <v-list-item disabled>
              {{ $t('routes.node.copyDialogNoMoreSubFolders') }}
            </v-list-item>
          </template>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="!!browseNode"
          v-on:click="displayNode(browseNode.parentid)"
          variant="text"
          density="comfortable"
          data-cy="dialog-copySelected-back-btn"
        >
          <v-icon>mdi-arrow-left</v-icon>
          {{ $t('routes.node.copyDialogBack') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          elevation=0
          v-bind:disabled="submitDisabled"
          @click="submit"
          variant="flat"
          density="comfortable"
          data-cy="dialog-copySelected-submit-btn"
        >
          {{ $t('common.copy') }}
        </v-btn>
        <v-btn
          @click="dialog = false"
          variant="text"
          density="comfortable"
          data-cy="dialog-copySelected-cancel-btn"
        >
          {{ $t('common.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-else data-cy="dialog-copySelect-modal-unprocessable-files">
      <v-card-title class="d-flex justify-space-between bg-warning">
        {{ $t('routes.node.copySelectedUnprocessableFilesTitle') }}
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="scrollable mt-4">
        {{ $t('routes.node.copySelectedUnprocessableFilesSubtitle') }}
        <ul class="unprocessable-list">
          <li v-for="pod in unprocessablePods" :key="pod.id">
            <router-link :to="{ name: 'pod:view', params: { id: pod.id } }" target="_blank">
              {{ pod.name }} {{ pod.versionText ? `(${pod.versionText})` : '' }}
            </router-link>
          </li>

          <li v-for="(group, idx) in deepUnprocessablePods" :key="`group_${idx}`">
            {{ group.path.join(' / ') }}
            <ul>
              <li v-for="pod in group.pods" :key="pod.id">
                <router-link :to="{ name: 'pod:view', params: { id: pod.id } }" target="_blank">
                  {{ pod.name }} {{ pod.versionText ? `(${pod.versionText})` : '' }}
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn
          @click="dialog = false"
          variant="text"
          density="comfortable"
          data-cy="dialog-copySelected-unprocessable-files-ok-btn"
        >
          {{ $t('common.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useAuthStore } from '@/stores/AuthStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import DepthExceededChildren from '@/components/DepthExceededChildren.vue';
import FolderCreateInline from '@/components/dialogs/FolderCreateInline.vue';

export default {
  mixins: [DialogActionKeys, DialogModel],
  components: { DepthExceededChildren, FolderCreateInline },
  name: "CopySelectedDialog",
  props: {
    nodes: {
      type: Array,
      required: false,
    },
    pods: {
      type: Array,
      required: false
    },
    isDisabled: {
      type: Boolean,
      required: false,
    },
  },
  data: function() {
    return {
      errorMessage: '',
      errorCode: null,
      errorParams: {},
      conflictingNodeNames: [],
      errorItems: null,
      browseNode: null,
      listGroupSelected: null,
      preventNoItems: false,
      archiveItems: false,
      preventDoubleClick: false,
      searchQuery: '',
      unprocessablePods: [],
      deepUnprocessablePods: [],
      isGettingNodeInfo: false,
    }
  },
  watch: {
    dialog: function(newValue) {
      this.resetDialog();
      if (!newValue) this.browseNode = null;
      if (newValue) this.preventNoItems = false;
    },
    browseNode: function (newValue) {
      if (!newValue) this.archiveItems = false;
      if (newValue && newValue.archived && newValue.archived === 1) {
        this.archiveItems = true;
      }
    },
  },
  methods: {
    resetDialog: function() {
      this.errorMessage = '';
      this.errorCode = null;
      this.errorParams = {};
      this.conflictingNodeNames = [];
      this.errorItems = null;
      this.listGroupSelected = null;
      this.preventDoubleClick = false;
      this.unprocessablePods = [];
      this.deepUnprocessablePods = [];
    },
    displayNode: async function(nodeID) {
      this.listGroupSelected = null;

      // sometimes this.browseNode.parentid is null
      if (nodeID) {
        this.isGettingNodeInfo = true;

        try {
          const nodeStore = useNodeStore();
          this.browseNode = await nodeStore.getNodeInfo(nodeID);
        } finally {
          this.isGettingNodeInfo = false;
        }

        this.listGroupSelected = null;
      } else {
        this.browseNode = null;
      }
    },
    async handleFolderCreated(createdNode) {
      if (!createdNode?.id) {
        return;
      }

      await this.displayNode(createdNode.id);
    },
    submit: function() {
      this.errorMessage = '';
      this.preventNoItems = true;
      if (!this.browseNode || this.itemsCount == 0 || this.preventDoubleClick) {
        return;
      }

      this.preventDoubleClick = true;

      let promises = [];

      if (this.nodes.length > 0) {
        const nodeStore = useNodeStore();
        if (this.isCrossTenantCopy) {
          const payload = {
            targetnodeid: this.browseNode.id,
            nodes: this.nodes.map(x => x.id),
          };
          promises.push(nodeStore.copyManyNodesCrossTenant(payload));
        } else {
          const payload = {
            targetnodeid: this.browseNode.id,     // destination
            nodeid: this.getCurrentNode.id,       // current directory
            nodes: this.nodes.map(x => x.id),
            archive: this.archiveItems ? 1 : 0,
          };
          promises.push(nodeStore.copyManyNodes(payload));
        }
      } else {
        promises.push(Promise.resolve());
      }

      if (this.pods.length > 0) {
        const podStore = usePodStore();
        if (this.isCrossTenantCopy) {
          const payload = {
            targetnodeid: this.browseNode.id,
            pods: this.pods.map(x => x.id),
          };
          promises.push(podStore.copyManyPodsCrossTenant(payload));
        } else {
          const payload = {
            targetnodeid: this.browseNode.id,     // destination
            nodeid: this.getCurrentNode.id,       // current directory
            pods: this.pods.map(x => x.id),
            archive: this.archiveItems ? 1 : 0,
          };
          promises.push(podStore.copyManyPods(payload));
        }
      } else {
        promises.push(Promise.resolve());
      }

      Promise.all(promises).then(([copyNodesResult, copyPodsResult]) => {
        if (copyNodesResult) {
          const nodeStore = useNodeStore();
          nodeStore._addOrUpdateNodes(copyNodesResult.copiedNodes);

          if (copyNodesResult.unprocessablePods.length > 0) {
            this.deepUnprocessablePods = copyNodesResult.unprocessablePods.sort((a, b) => a.pods.length - b.pods.length);
          }
        }

        if (copyPodsResult?.unprocessablePods.length > 0) {
          this.unprocessablePods = copyPodsResult.unprocessablePods;
        }
      }).then(() => {
        this.$showMessage(this.$t('routes.node.copyFileInfo'), { type: 'info' });

        if (this.unprocessablePods.length === 0 && this.deepUnprocessablePods.length === 0) {
          this.dialog = false;
          this.resetDialog();
        }
      }).catch((error) => {
        const err = error?.response?.data?.error;
        if (err?.code === 'ERR233') {
          this.errorCode = err.code;
          this.errorParams = { ...err.params };
          if (err.extra?.items) {
            this.errorItems = err.extra.items;
          }
        } else {
          this.errorMessage = this.$localizeMessage(err.message, this?.$t('backend.system-error') || 'System error!');
        }
        console.error(error);
      });
    },
  },
  computed: {
    nodeList: function() {
      if (this.browseNode) {
        if (!this.browseNode.children) {
          return [];
        }

        const childNodes = [];

        // filter out archived nodes
        if (Array.isArray(this.browseNode.children) && this.browseNode.children.length > 0) {
          // filter out archived nodes
          for (let i = 0; i < this.browseNode.children.length; i++) {
            const childNode = this.browseNode.children[i];

            if (!childNode.archived) {
              childNodes.push(childNode);
            }
          }

          // filter by search query
          if (this.searchQuery.length >= 1) {
            const cleanQuery = this.searchQuery.toLowerCase();

            const filteredNodes = childNodes.filter((node) => {
              return node.name.toLowerCase().includes(cleanQuery);
            });

            // sort the nodes by name
            return (filteredNodes.length > 0) ? filteredNodes.sort((a, b) => a.name.localeCompare(b.name)) : [];
          }
        }

        return childNodes.sort((a, b) => a.name.localeCompare(b.name));
      }

      if (this.accessPoints && Array.isArray(this.accessPoints) && this.accessPoints.length > 0) {
        const accessPoints = this.accessPoints;
        return accessPoints.sort((a, b) => a.name.localeCompare(b.name)) || [];
      }

      return [];
    },
    accessPoints: function() {
      const authStore = useAuthStore();
      return authStore.getAccesspoints.map(ap => ap.node);
    },
    getViewPod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    getCurrentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    itemsCount: function() {
      return this.nodes.length + this.pods.length;
    },
    getNodeType: function() {
      const commonStore = useCommonStore();
      const TYPES = commonStore.nodeTypes;

      let self = this;
      return (node) => {
        switch (node.type) {
          case TYPES.FOLDER: return self.$t('routes.node.typeFolder');
          case TYPES.PROJECT: return self.$t('routes.node.typeProject');
          case TYPES.CLIENT: return self.$t('routes.node.typeClient');
          case TYPES.TENANT: return self.$t('routes.node.typeTenant');
          default: return self.$t('routes.node.typeUnknown');
        }
      }
    },
    getNodeIconName: function() {
      return (node) => {
        let iconName;
        switch (node.type) {
          case 2: iconName = "mdi-briefcase-clock-outline"; break;
          case 3: iconName = "tw tw-office-building"; break;
          case 4: iconName = "mdi-home-assistant"; break;
          default: iconName = "mdi-folder-outline"; break;
        }

        if (node.archived == 1 || node.archived == 2) {
          iconName = "mdi-package-down";
        }

        return iconName;
      };
    },
    isNodeDisabled: function() {
      const commonStore = useCommonStore();
      const TYPES = commonStore.nodeTypes;
      const rootNodeUUID = commonStore.rootFolderId;

      let selectedNodeTypes = this.nodes.map(x => x.type);

      return (node) => {
        // The parent of the tenant node could be only the root node.
        if (selectedNodeTypes.indexOf(TYPES.TENANT) >= 0 && node.id !== rootNodeUUID) {
          return true;
        }
        // The parent of the client node could be only a tenant.
        return selectedNodeTypes.indexOf(TYPES.CLIENT) >= 0 && node.type !== TYPES.TENANT && node.id !== rootNodeUUID;
      };
    },
    isCrossTenantCopy: function() {
      if (!this.browseNode) return false;
      const authStore = useAuthStore();
      const rootNodeId = authStore.rootNodeId;
      return !this.browseNode.rootpath.startsWith(`/${rootNodeId}`);
    },
    submitDisabled: function() {
      return !this.browseNode || this.isGettingNodeInfo || this.itemsCount === 0 || this.preventDoubleClick;
    },
    shouldShowNoItems: function() {
      return !this.errorMessage && !this.preventNoItems;
    },
    isArchived: function() {
      return (this.browseNode?.archived === 1);
    },
  },
}
</script>

<style>
.v-subheader > .copy-selected-dialog-list-subheader {
  font-size: 1rem;
}

.v-subheader.v-theme--light > .copy-selected-dialog-list-subheader {
  color: rgba(0, 0, 0, 0.8);
}

.unprocessable-list {
  margin-top: 8px;
  padding-left: 20px;
}

.unprocessable-list ul {
  padding-left: 20px;
  margin-top: 4px;
}
</style>
